import React, { useMemo, useState } from "react";
import { Alert, Spinner } from "react-bootstrap";
import API from "../services/api";
import { formatEmployeeId } from "../utils/idFormat";
import "./AdminEmployeeAttendanceReport.css";

const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" }) : "—";

const formatTime = (d) =>
  d ? new Date(d).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "--:--";

const initials = (name = "") => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean).slice(0, 2);
  if (!parts.length) return "NA";
  return parts.map((p) => p[0].toUpperCase()).join("");
};

const statusClass = (status = "") => String(status).toLowerCase().replace(/\s+/g, "-");

const hoursFromRecord = (r) => {
  const inTime = r?.punchIn?.time ? new Date(r.punchIn.time).getTime() : null;
  const outTime = r?.punchOut?.time ? new Date(r.punchOut.time).getTime() : null;
  if (!inTime || !outTime || outTime <= inTime) return "0h 00m";
  const mins = Math.floor((outTime - inTime) / 60000);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}h ${String(m).padStart(2, "0")}m`;
};

const AdminEmployeeAttendanceReport = () => {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [search, setSearch] = useState("");
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const handleFetch = async () => {
    if (!from || !to) return setMessage("Please select both dates");
    try {
      setLoading(true);
      const res = await API.get(`/admin/attendance-summary?from=${from}&to=${to}`);
      setRecords(Array.isArray(res.data) ? res.data : []);
      setPage(1);
      setMessage("");
    } catch (err) {
      console.error(err);
      setMessage("Failed to fetch records");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!from || !to) return setMessage("Please select both dates before exporting");
    try {
      const res = await API.get(`/admin/attendance/export?from=${from}&to=${to}`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `employee_attendance_${from}_to_${to}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
      setMessage("");
    } catch (err) {
      console.error(err);
      setMessage("Failed to export CSV");
    }
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return records;
    return records.filter((r) => {
      const name = String(r.employee?.fullName || "").toLowerCase();
      const mobile = String(r.employee?.mobile || "");
      return name.includes(q) || mobile.includes(q);
    });
  }, [records, search]);

  const stats = useMemo(() => {
    let present = 0;
    let absent = 0;
    let late = 0;
    filtered.forEach((r) => {
      const s = String(r.status || "").toLowerCase();
      if (s === "present") present += 1;
      else if (s === "absent") absent += 1;
      else if (s.includes("late")) late += 1;
    });
    return {
      total: filtered.length,
      present,
      absent,
      late,
    };
  }, [filtered]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageRows = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  return (
    <div className="aer-page">
      <header className="aer-header">
        <div className="aer-header-inner">
          <div className="aer-brand">
            <div className="aer-brand-icon">
              <span className="material-symbols-outlined">event_available</span>
            </div>
            <h1>Employee Attendance Report</h1>
          </div>
          <div className="aer-top-right">
            <button className="aer-icon-btn" type="button">
              <span className="material-symbols-outlined">notifications</span>
            </button>
            <div className="aer-admin-badge">AD</div>
          </div>
        </div>
      </header>

      <main className="aer-main">
        <section className="aer-filter-card">
          <div className="aer-filters">
            <div className="aer-filter">
              <label>From Date</label>
              <div className="aer-input-wrap">
                <span className="material-symbols-outlined">calendar_today</span>
                <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
              </div>
            </div>
            <div className="aer-filter">
              <label>To Date</label>
              <div className="aer-input-wrap">
                <span className="material-symbols-outlined">calendar_today</span>
                <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
              </div>
            </div>
            <div className="aer-filter aer-filter-grow">
              <label>Search Employee</label>
              <div className="aer-input-wrap">
                <span className="material-symbols-outlined">search</span>
                <input
                  type="text"
                  placeholder="Search by name or mobile number..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                />
              </div>
            </div>
            <div className="aer-actions">
              <button className="aer-btn primary" onClick={handleFetch} type="button">
                <span className="material-symbols-outlined">refresh</span>
                Fetch Report
              </button>
              <button className="aer-btn success" onClick={handleDownload} type="button">
                <span className="material-symbols-outlined">download</span>
                Export CSV
              </button>
            </div>
          </div>
        </section>

        {message ? <Alert variant="danger">{message}</Alert> : null}

        <section className="aer-stats">
          <div className="aer-stat">
            <div className="icon blue"><span className="material-symbols-outlined">groups</span></div>
            <div><p>Total Employees</p><h3>{stats.total}</h3></div>
          </div>
          <div className="aer-stat">
            <div className="icon green"><span className="material-symbols-outlined">check_circle</span></div>
            <div><p>Present Today</p><h3 className="green">{stats.present}</h3></div>
          </div>
          <div className="aer-stat">
            <div className="icon red"><span className="material-symbols-outlined">cancel</span></div>
            <div><p>Absent Today</p><h3 className="red">{stats.absent}</h3></div>
          </div>
          <div className="aer-stat">
            <div className="icon amber"><span className="material-symbols-outlined">schedule</span></div>
            <div><p>Late Arrivals</p><h3 className="amber">{stats.late}</h3></div>
          </div>
        </section>

        <section className="aer-table-shell">
          {loading ? (
            <div className="aer-loading"><Spinner animation="border" /></div>
          ) : filtered.length === 0 ? (
            <div className="aer-empty">
              <span className="material-symbols-outlined">search_off</span>
              <h3>No records found</h3>
              <p>Try adjusting your filters or search term.</p>
            </div>
          ) : (
            <>
              <div className="aer-table-wrap">
                <table className="aer-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Mobile</th>
                      <th>Status</th>
                      <th className="center">Punch In</th>
                      <th className="center">Punch Out</th>
                      <th className="center">Total Hours</th>
                      <th>Date</th>
                      <th className="right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((r, i) => {
                      const name = r.employee?.fullName || "Unknown";
                      const mobile = r.employee?.mobile || "-";
                      const status = r.status || "-";
                      return (
                        <tr key={`${r.employee?._id || mobile}-${i}`}>
                          <td>
                            <div className="aer-emp">
                              <div className="aer-avatar">{initials(name)}</div>
                              <div>
                                <p>{name}</p>
                                <small>ID: {r.employee ? formatEmployeeId(r.employee) : "NA"}</small>
                              </div>
                            </div>
                          </td>
                          <td>{mobile}</td>
                          <td>
                            <span className={`aer-status ${statusClass(status)}`}>{status}</span>
                          </td>
                          <td className="center">{formatTime(r.punchIn?.time)}</td>
                          <td className="center">{formatTime(r.punchOut?.time)}</td>
                          <td className="center strong">{hoursFromRecord(r)}</td>
                          <td>{formatDate(r.date)}</td>
                          <td className="right">
                            <button className="aer-icon-btn" type="button">
                              <span className="material-symbols-outlined">visibility</span>
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="aer-foot">
                <p>
                  Showing <b>{Math.min((safePage - 1) * pageSize + 1, filtered.length)}</b>-
                  <b>{Math.min(safePage * pageSize, filtered.length)}</b> of <b>{filtered.length}</b> records
                </p>
                <div className="aer-pager">
                  <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage === 1}>
                    <span className="material-symbols-outlined">chevron_left</span>
                  </button>
                  <span className="current">{safePage}</span>
                  <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={safePage === totalPages}>
                    <span className="material-symbols-outlined">chevron_right</span>
                  </button>
                </div>
              </div>
            </>
          )}
        </section>
      </main>
    </div>
  );
};

export default AdminEmployeeAttendanceReport;
